import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { IoClose, IoSearch } from "react-icons/io5";
import { isValidPhoneNumber } from "libphonenumber-js";
import axios from "axios";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Card from "components/card";
import InputField from "components/fields/InputField";
import CreateFolderModal from "components/modal/CreateFolderModal";
import { API_BASE_URL } from "utils/api";
import { getCareOfList } from "views/admin/careOfManagement/careOfApi";

const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Urgent"];

const AddLeadModal = ({ isOpen, onClose, onSave }) => {
  const dropdownRef = useRef();
  const careOfInputRef = useRef();
  const careOfDropdownPortalRef = useRef();
  const folderDropdownRef = useRef();
  const folderDropdownPortalRef = useRef();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    careOf: "",
    folderId: "",
    priority: "Medium",
    profileImage: null,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [mounted, setMounted] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [foldersList, setFoldersList] = useState([]);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [careOfSearch, setCareOfSearch] = useState("");
  const [careOfOptions, setCareOfOptions] = useState([]);
  const [careOfLoading, setCareOfLoading] = useState(false);
  const [showCareOfDropdown, setShowCareOfDropdown] = useState(false);
  const [selectedCareOfName, setSelectedCareOfName] = useState("");
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [folderSearch, setFolderSearch] = useState("");

  const [selectedFolderName, setSelectedFolderName] = useState("");

  const [careOfCleared, setCareOfCleared] = useState(false);
  const [folderCleared, setFolderCleared] = useState(false);

  // For CareOf dropdown portal (must be at top level)
  const [careOfDropdownPos, setCareOfDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  // For folder dropdown portal (must be at top level)
  const folderInputRef = useRef();
  const [folderDropdownPos, setFolderDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (showCareOfDropdown && careOfInputRef.current) {
      const rect = careOfInputRef.current.getBoundingClientRect();
      setCareOfDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [showCareOfDropdown]);

  useEffect(() => {
    if (showFolderDropdown && folderInputRef.current) {
      const rect = folderInputRef.current.getBoundingClientRect();
      setFolderDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [showFolderDropdown]);

  useEffect(() => {
    if (careOfCleared) {
      const t = setTimeout(() => setCareOfCleared(false), 2500);
      return () => clearTimeout(t);
    }
  }, [careOfCleared]);

  useEffect(() => {
    if (folderCleared) {
      const t = setTimeout(() => setFolderCleared(false), 2500);
      return () => clearTimeout(t);
    }
  }, [folderCleared]);

  const filteredFolderOptions = folderSearch.trim() === ""
    ? foldersList
    : foldersList.filter((folder) =>
      folder.name.toLowerCase().includes(folderSearch.toLowerCase())
    );
  const hasCareOfSearch = careOfSearch.trim() !== "";
  const noCareOfResults = hasCareOfSearch && !careOfLoading && careOfOptions.length === 0;
  const noFolderResults = folderSearch.trim() !== "" && filteredFolderOptions.length === 0;

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      careOf: "",
      folderId: "",
      profileImage: null,
    });
    setErrors({});
    setImagePreview(null);
    setCareOfSearch("");
    setCareOfOptions([]);
    setShowCareOfDropdown(false);
    setSelectedCareOfName("");
    setShowFolderDropdown(false);
    setFolderSearch("");
    setSelectedFolderName("");
  }, []);

  const fetchCareOfOptions = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) {
      setCareOfOptions([]);
      return;
    }
    setCareOfLoading(true);
    try {
      const result = await getCareOfList({ page: 1, limit: 10, filters: { search: searchTerm } });
      setCareOfOptions(result.items || []);
    } catch (error) {
      console.error("Failed to fetch careOf options:", error);
      setCareOfOptions([]);
    } finally {
      setCareOfLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCareOfOptions(careOfSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [careOfSearch, fetchCareOfOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !(careOfDropdownPortalRef.current && careOfDropdownPortalRef.current.contains(event.target))
      ) {
        setShowCareOfDropdown(false);
      }
      if (
        folderDropdownRef.current &&
        !folderDropdownRef.current.contains(event.target) &&
        !(folderDropdownPortalRef.current && folderDropdownPortalRef.current.contains(event.target))
      ) {
        setShowFolderDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNewFolderCreated = (newFolder) => {
    if (!newFolder) return;
    const folderObj = { id: newFolder._id || newFolder.id || newFolder._id, name: newFolder.name || newFolder.title || newFolder.name };
    setFoldersList((prev) => [...prev, folderObj]);
    // auto-select newly created folder (single selection dropdown -> keep as single-item array)
    setFormData((prev) => ({ ...prev, folderId: folderObj.id }));
    setSelectedFolderName(folderObj.name);
    setFolderSearch("");
    setShowFolderDropdown(false);
    setIsCreateFolderOpen(false);
  };

  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE_URL}/folders`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok) return;
        const result = await res.json();
        const data = result?.data?.allFolders || result?.data || result || [];
        const arr = Array.isArray(data) ? data : data.data || [];
        const filtered = arr.filter((f) => String(f.type || "").toLowerCase() === "lead");
        setFoldersList(filtered.map((f) => ({ id: f._id, name: f.name })));
      } catch (err) {
        // silent fail - folders optional
        console.error("Failed to fetch folders", err);
      }
    };

    if (isOpen) fetchFolders();
  }, [isOpen]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
    } else {
      setTimeout(() => setMounted(false), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const firstInput = document.getElementById("name");
      if (firstInput) firstInput.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  const SUPPORTED_IMAGE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
  ];
  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB

  const validateForm = () => {
    const newErrors = {};

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (formData.phone.trim() && !isValidPhoneNumber(formData.phone)) {
      newErrors.phone =
        "Invalid phone number format. Use international format, e.g. +1234567890";
    }

    if (formData.profileImage && !SUPPORTED_IMAGE_TYPES.includes(formData.profileImage.type)) {
      newErrors.profileImage = "Unsupported image type. Use JPG, PNG, WEBP, GIF or SVG.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // If user typed a CareOf but didn't select an option, clear the typed input
    // so we don't submit an unwanted freeform value and show a transient hint.
    if (!formData.careOf) {
      setCareOfSearch("");
      setSelectedCareOfName("");
      setCareOfCleared(true);
    }

    if (!validateForm()) return;

    // Clear folder input if no folder selected (prevents submitting unwanted typed text)
    if (!formData.folderId) {
      setFolderSearch("");
      setSelectedFolderName("");
      setFolderCleared(true);
    }

    setLoading(true);
    try {
      const payload = new FormData();
      if (formData.name.trim()) payload.append("name", formData.name);
      if (formData.email.trim()) payload.append("email", formData.email);
      if (formData.phone.trim()) payload.append("phone", formData.phone);
      if (formData.careOf) payload.append("careOf", formData.careOf);
      if (formData.folderId) payload.append("folderId", formData.folderId);
      if (formData.priority) payload.append("priority", formData.priority);
      if (formData.profileImage) payload.append("profileImage", formData.profileImage);

      const token = localStorage.getItem("token");
      await axios.post(`${API_BASE_URL}/leads`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      handleClose();
      if (onSave) {
        await onSave();
      }
    } catch (error) {
      console.error("Error saving lead:", error);
      const apiErrors = error?.response?.data?.errors;

      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        const mappedErrors = {};
        apiErrors.forEach((item) => {
          if (item?.path && item?.msg) {
            mappedErrors[item.path] = item.msg;
          }
        });

        setErrors((prev) => ({ ...prev, ...mappedErrors }));
      } else {
        setErrors((prev) => ({
          ...prev,
          submit: error?.response?.data?.message || "Failed to create lead",
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file && !SUPPORTED_IMAGE_TYPES.includes(file.type)) {
      setFormData((prev) => ({ ...prev, profileImage: null }));
      setImagePreview(null);
      setErrors((prev) => ({
        ...prev,
        profileImage: "Unsupported image type. Use JPG, JPEG, or PNG.",
      }));
      return;
    }

    if (file && file.size > MAX_IMAGE_SIZE) {
      setFormData((prev) => ({ ...prev, profileImage: null }));
      setImagePreview(null);
      setErrors((prev) => ({
        ...prev,
        profileImage: "File too large. Maximum size is 10 MB.",
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, profileImage: file || null }));

    if (errors.profileImage) {
      setErrors((prev) => ({ ...prev, profileImage: "" }));
    }

    if (file) {
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  // Minimal modal - no country/state/district/priority/source logic

  if (!mounted) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 transition-all duration-300 ease-out ${isOpen ? "backdrop-blur-md opacity-100" : "backdrop-blur-none opacity-0"
        }`}
    // Removed onClick={handleClose} to prevent closing on background click
    >
      <style>{`.PhoneInputInput:focus, .PhoneInput input:focus { outline: none !important; box-shadow: none !important; border: none !important; }`}</style>
      <Card
        extra={`w-full max-w-2xl p-6 max-h-[80vh] overflow-hidden transition-all duration-300 ease-out ${isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"
          }`}
        // Prevent click inside modal from closing it
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">Add New Lead</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <IoClose className="h-6 w-6" />
          </button>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          <div>
            <InputField
              label="Name"
              id="name"
              type="text"
              placeholder="Enter lead name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              maxLength={25}
              state={errors.name ? "error" : ""}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1 ml-3">{errors.name}</p>}
          </div>

          <div>
            <InputField
              label="Email"
              id="email"
              type="email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              maxLength={30}
              state={errors.email ? "error" : ""}
            />
            {errors.email && <p className="text-xs text-red-500 mt-1 ml-3">{errors.email}</p>}
          </div>

          <div>
            <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">Phone</label>
            <PhoneInput
              international
              defaultCountry="IN"
              value={formData.phone}
              onChange={(value) => handleInputChange("phone", value || "")}
              className={`mt-2 flex h-12 w-full items-center rounded-xl border bg-white/0 p-3 text-sm outline-none ${errors.phone ? "border-red-500 text-red-500 dark:!border-red-400" : "border-gray-200 dark:border-white/10 dark:text-white"}`}
              inputClassName="h-full w-full bg-transparent outline-none focus:outline-none border-none"
              inputProps={{ style: { outline: 'none', boxShadow: 'none', border: 'none', background: 'transparent' } }}
            />
            {errors.phone && <p className="text-xs text-red-500 mt-1 ml-3">{errors.phone}</p>}
          </div>

          <div ref={dropdownRef} className="relative">
            <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">CareOf</label>
            <div className="mt-2 relative">
              <input
                ref={careOfInputRef}
                type="text"
                placeholder="Search care of"
                value={selectedCareOfName || careOfSearch}
                onChange={(e) => {
                  const value = e.target.value;
                  if (selectedCareOfName) {
                    setSelectedCareOfName("");
                    setFormData((prev) => ({ ...prev, careOf: "" }));
                  }
                  setCareOfSearch(value);
                  setShowCareOfDropdown(true);
                }}
                onFocus={() => setShowCareOfDropdown(true)}
                className={`flex h-12 w-full items-center rounded-xl border bg-white/0 p-3 pl-10 pr-10 text-sm outline-none dark:text-white ${errors.careOf ? "border-red-500" : "border-gray-200 dark:border-white/10"
                  }`}
              />
              <IoSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              {selectedCareOfName && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, careOf: "" }));
                    setSelectedCareOfName("");
                    setCareOfSearch("");
                    setShowCareOfDropdown(false);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  <IoClose className="h-4 w-4" />
                </button>
              )}
            </div>
            {errors.careOf && <p className="text-xs text-red-500 mt-1 ml-3">{errors.careOf}</p>}
            {careOfCleared && !errors.careOf && (
              <p className="text-xs text-yellow-600 mt-1 ml-3">Cleared invalid CareOf input</p>
            )}
          </div>
          {showCareOfDropdown && careOfInputRef.current && ReactDOM.createPortal(
            <div
              ref={careOfDropdownPortalRef}
              style={{
                position: "absolute",
                top: careOfDropdownPos.top,
                left: careOfDropdownPos.left,
                width: careOfDropdownPos.width,
                zIndex: 9999,
              }}
              className="rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800 max-h-48 overflow-y-auto"
            >
              {careOfLoading ? (
                <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">Loading...</p>
              ) : noCareOfResults ? (
                <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">No results found</p>
              ) : careOfOptions.length === 0 ? (
                <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">Type to search..</p>
              ) : (
                careOfOptions.map((option) => (
                  <button
                    type="button"
                    key={option.id}
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, careOf: option.id }));
                      setSelectedCareOfName(option.name);
                      setCareOfSearch("");
                      setShowCareOfDropdown(false);
                    }}
                    className="h-8 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                  >
                    {option.name}
                  </button>
                ))
              )}
            </div>,
            document.body
          )}

          <div ref={folderDropdownRef} className="relative">
            <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">Folder</label>
            <div className="mt-2 flex items-center gap-2 relative">
              <div className="flex-1 relative">
                <input
                  ref={folderInputRef}
                  type="text"
                  placeholder="Search or select folder"
                  value={(() => {
                    if (selectedFolderName) return selectedFolderName;
                    if (formData.folderId) {
                      const found = foldersList.find((f) => f.id === formData.folderId);
                      if (found) return found.name;
                    }
                    return folderSearch;
                  })()}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedFolderName("");
                    setFormData((prev) => ({ ...prev, folderId: "" }));
                    setFolderSearch(value);
                    setShowFolderDropdown(true);
                  }}
                  onFocus={() => setShowFolderDropdown(true)}
                  className={`flex h-12 w-full items-center rounded-xl border bg-white/0 p-3 pl-10 pr-10 text-sm outline-none dark:text-white ${errors.folderId ? "border-red-500" : "border-gray-200 dark:border-white/10"
                    }`}
                />
                <IoSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                {selectedFolderName && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, folderId: "" }));
                      setSelectedFolderName("");
                      setFolderSearch("");
                      setShowFolderDropdown(false);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    <IoClose className="h-4 w-4" />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsCreateFolderOpen(true)}
                className="rounded-lg bg-brand-500 px-3 py-2 text-sm text-white whitespace-nowrap"
              >
                Add
              </button>
            </div>
            {/* Folder Dropdown Portal */}
            {showFolderDropdown && ReactDOM.createPortal(
              <div
                ref={folderDropdownPortalRef}
                style={{
                  position: "absolute",
                  top: folderDropdownPos.top,
                  left: folderDropdownPos.left,
                  width: folderDropdownPos.width,
                  zIndex: 9999,
                }}
                className="rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800 max-h-48 overflow-y-auto"
              >
                {foldersList.length === 0 ? (
                  <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">No folders available</p>
                ) : noFolderResults ? (
                  <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">No folders found</p>
                ) : folderSearch.trim() === "" ? (
                  foldersList.map((folder) => (
                    <button
                      type="button"
                      key={folder.id}
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, folderId: folder.id }));
                        setSelectedFolderName(folder.name);
                        setFolderSearch("");
                        setShowFolderDropdown(false);
                      }}
                      className="h-8 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                    >
                      {folder.name}
                    </button>
                  ))
                ) : (
                  foldersList
                    .filter((folder) =>
                      folder.name.toLowerCase().includes(folderSearch.toLowerCase())
                    )
                    .map((folder) => (
                      <button
                        type="button"
                        key={folder.id}
                        onClick={() => {
                          setFormData((prev) => ({ ...prev, folderId: folder.id }));
                          setSelectedFolderName(folder.name);
                          setFolderSearch("");
                          setShowFolderDropdown(false);
                        }}
                        className="h-8 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                      >
                        {folder.name}
                      </button>
                    ))
                )}
              </div>,
              document.body
            )}
            {errors.folderId && <p className="text-xs text-red-500 mt-1 ml-3">{errors.folderId}</p>}
            {folderCleared && !errors.folderId && (
              <p className="text-xs text-yellow-600 mt-1 ml-3">Cleared invalid folder input</p>
            )}
          </div>

          <div>
            <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">Priority</label>
            <select
              id="priority"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="mt-2 flex h-12 w-full items-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:border-white/10 dark:text-white"
            >
              {PRIORITY_OPTIONS.map((priority) => (
                <option key={priority} value={priority} className="dark:bg-gray-800">
                  {priority}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">Profile Image</label>
            <label
              htmlFor="profileImage"
              className={`mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none dark:text-white cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${errors.profileImage ? "border-red-500 text-red-500 dark:!border-red-400" : "border-gray-200 dark:border-white/10"}`}
            >
              Choose File
            </label>
            <input id="profileImage" type="file" accept="image/jpeg,image/jpg,image/png" onChange={handleFileChange} className="hidden" />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 ml-3">
              Maximum file size 10MB. Accepted formats: JPG, JPEG, PNG.
            </p>
            {imagePreview && <img src={imagePreview} alt="Profile Preview" className="mt-2 w-16 h-16 object-cover rounded" />}
            {errors.profileImage && <p className="text-xs text-red-500 mt-1 ml-3">{errors.profileImage}</p>}
          </div>
        </div>

        {errors.submit && <p className="mt-4 text-sm text-red-500">{errors.submit}</p>}

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Lead"}
          </button>
        </div>
      </Card>
      <CreateFolderModal isOpen={isCreateFolderOpen} onClose={() => setIsCreateFolderOpen(false)} onSubmit={handleNewFolderCreated} folderType="Lead" />
    </div>
  );
};

export default AddLeadModal;